/**
 * Utility function to sort form responses based on the original form design order
 * This ensures that sections and questions are displayed in the same order
 * as they were in the form when it was answered.
 */

interface FormSection {
  id: string;
  name?: string;
  questions: Array<{ id: string; [key: string]: any }>;
}

interface SectionResponse {
  sectionId: string;
  sectionName: string;
  questions?: Record<string, any>;
  responses?: Record<string, any>;
}

/**
 * Sorts form responses based on the form design order
 * @param responses - The responses object (Record<string, SectionResponse>)
 * @param formDesign - The form design array containing sections in order
 * @returns Sorted array of section responses
 */
export function sortFormResponses(
  responses: Record<string, SectionResponse> | SectionResponse[],
  formDesign: FormSection[] | null | undefined
): SectionResponse[] {
  // If responses is already an array, convert to object first
  let responsesObj: Record<string, SectionResponse>;
  if (Array.isArray(responses)) {
    responsesObj = {};
    responses.forEach((section) => {
      if (section.sectionId) {
        responsesObj[section.sectionId] = section;
      }
    });
  } else {
    responsesObj = responses || {};
  }

  // If no form design is provided, return responses as array (preserving object order if possible)
  if (!formDesign || !Array.isArray(formDesign) || formDesign.length === 0) {
    return Object.values(responsesObj);
  }

  // Create a map of section IDs to their order in the form design
  const sectionOrderMap = new Map<string, number>();
  formDesign.forEach((section, index) => {
    if (section.id) {
      sectionOrderMap.set(section.id, index);
    }
  });

  // Sort sections based on form design order
  const sortedSections = Object.values(responsesObj).sort((a, b) => {
    const orderA = sectionOrderMap.get(a.sectionId) ?? Infinity;
    const orderB = sectionOrderMap.get(b.sectionId) ?? Infinity;
    return orderA - orderB;
  });

  // Sort questions within each section based on form design order
  return sortedSections.map((section) => {
    // Find the corresponding section in form design
    const designSection = formDesign.find((s) => s.id === section.sectionId);
    
    if (!designSection || !designSection.questions) {
      // If no matching section in design, return as is
      return section;
    }

    // Get questions from section (could be in questions or responses property)
    const questions = section.questions || section.responses || {};
    
    // If questions is already an array, sort it
    if (Array.isArray(questions)) {
      const questionOrderMap = new Map<string, number>();
      designSection.questions.forEach((question, index) => {
        if (question.id) {
          questionOrderMap.set(question.id, index);
        }
      });

      const sortedQuestions = [...questions].sort((a, b) => {
        const orderA = questionOrderMap.get(a.questionId) ?? Infinity;
        const orderB = questionOrderMap.get(b.questionId) ?? Infinity;
        return orderA - orderB;
      });

      return {
        ...section,
        questions: sortedQuestions,
      };
    }

    // If questions is an object, convert to array and sort
    if (typeof questions === 'object' && questions !== null) {
      const questionOrderMap = new Map<string, number>();
      designSection.questions.forEach((question, index) => {
        if (question.id) {
          questionOrderMap.set(question.id, index);
        }
      });

      // Convert object to array and sort
      const questionsArray = Object.values(questions).sort((a: any, b: any) => {
        const orderA = questionOrderMap.get(a.questionId) ?? Infinity;
        const orderB = questionOrderMap.get(b.questionId) ?? Infinity;
        return orderA - orderB;
      });

      return {
        ...section,
        questions: questionsArray,
      };
    }

    return section;
  });
}

/**
 * Sorts questions within a section based on form design order
 * @param questions - Questions object or array
 * @param formDesign - The form design array
 * @param sectionId - The section ID to find questions for
 * @returns Sorted array of questions
 */
export function sortSectionQuestions(
  questions: Record<string, any> | any[],
  formDesign: FormSection[] | null | undefined,
  sectionId: string
): any[] {
  if (!formDesign || !Array.isArray(formDesign)) {
    // If no form design, convert to array if needed
    return Array.isArray(questions) ? questions : Object.values(questions || {});
  }

  // Find the section in form design
  const designSection = formDesign.find((s) => s.id === sectionId);
  if (!designSection || !designSection.questions) {
    // If no matching section, convert to array if needed
    return Array.isArray(questions) ? questions : Object.values(questions || {});
  }

  // Create order map for questions
  const questionOrderMap = new Map<string, number>();
  designSection.questions.forEach((question, index) => {
    if (question.id) {
      questionOrderMap.set(question.id, index);
    }
  });

  // Convert to array if needed
  const questionsArray = Array.isArray(questions) 
    ? questions 
    : Object.values(questions || {});

  // Sort by order in form design
  return questionsArray.sort((a, b) => {
    const orderA = questionOrderMap.get(a.questionId) ?? Infinity;
    const orderB = questionOrderMap.get(b.questionId) ?? Infinity;
    return orderA - orderB;
  });
}

